// @givmo/app-charity — Compliance's section of the composed Settings page (CCM-R1-AI-SETTING): the
// module's declared settings (Org Machinery OM-17 seam B), registered beside `COMPLIANCE_NAV` in
// `consoleDeclarations()` and compiled in with the module's knob exactly as its routes are.
//
// Four panels, each declared on the string the backend's change requires — so the page mounts it
// for a Compliance Owner or a Global Admin and for nobody else (the seam's law is absence, never a
// disabled control). Each panel's `order` places it; the four:
//   • "Use AI on your organization’s data" (card 11), on the module's Owner string;
//   • "Sign-in check before signing or sending" (CCM-R1-SIGN-WEB; frame 48 in frame 82's words), on
//     `compliance:send_for_signature`, which is released only with signing: where signing is dark,
//     nobody holds it and the card is absent;
//   • "Givmo’s filing desk" (CCM-R1-DESK-WEB; REVIEW-35's desk-23 to desk-25), on the same string:
//     turning the organization's agreement off is a Compliance Owner's or a Global Admin's (J1);
//   • "Sign-in details on signing requests" (CCM-R1-SIGN-REDACT-UNFINISHED-REQUESTS (web)), on the
//     same string: everyone the organization's requests hold sign-in details for, each removable.
//
// A panel mounts outside the module's page gate, so it brings the host its card reads from: the
// organization the page is scoped to, the module's standing derived ONCE from the console's standing
// read (`complianceStandingFor`, the three signals the module's direct-link gate reads, so the two can
// never disagree), and the charity adapter.

import { useMemo } from 'react';
import {
  useOrgMembersExhausted,
  type ModuleSettings,
  type SettingsPanelProps,
} from '@givmo/org-console';
import {
  COMPLIANCE_OWNER_PERMISSION,
  COMPLIANCE_SEND_FOR_SIGNATURE_PERMISSION,
  COMPLIANCE_SLUG,
  ComplianceAiSettingCard,
  ComplianceProvider,
  ComplianceSigningLevelCard,
  ComplianceSigningPeopleCard,
  DeskStandingCard,
} from '@givmo/compliance';
import { useCharityConsole } from '../../console/charity-console-context';
import { useActiveOrg } from '../../org/active-org-context';
import {
  charityComplianceOperations,
  complianceStandingFor,
  organizationMembers,
  renderSetupOffer,
  rosterNames,
} from './compliance-adapter';

/**
 * "Use AI on your organization’s data" — the organization's setting and its change. The card names
 * who last changed it (D-CCM-96), from the roster the module's own host reads.
 */
export function ComplianceAiSettingPanel({ orgRef, registerDirty }: SettingsPanelProps) {
  const { pending, errored, standings } = useCharityConsole();
  const moduleStanding = complianceStandingFor({ pending, errored, standings });
  const roster = useOrgMembersExhausted(orgRef, moduleStanding === 'available');
  const personName = useMemo(() => rosterNames(roster.data), [roster.data]);
  return (
    <ComplianceProvider
      host={{
        orgRef,
        isReady: true,
        isPending: false,
        isError: false,
        error: null,
        moduleStanding,
        personName,
      }}
      operations={charityComplianceOperations}
    >
      <ComplianceAiSettingCard onDirtyChange={registerDirty} />
    </ComplianceProvider>
  );
}

/**
 * "Sign-in check before signing or sending" — the organization's level and its change. Lowering it
 * runs through the console's one "Confirm it's you" (the authenticated layout's step-up host), so
 * the prompt names the organization as the page does.
 */
export function ComplianceSigningLevelPanel({ orgRef, registerDirty }: SettingsPanelProps) {
  const { pending, errored, standings } = useCharityConsole();
  const { activeMembership } = useActiveOrg();
  const moduleStanding = complianceStandingFor({ pending, errored, standings });
  const organizationName = activeMembership?.name?.trim() ?? '';
  return (
    <ComplianceProvider
      host={{
        orgRef,
        isReady: true,
        isPending: false,
        isError: false,
        error: null,
        moduleStanding,
        ...(organizationName.length > 0 ? { organizationName } : {}),
        // AS-PWL-2e: where the level in force can't be met here, the card offers to set up a
        // passkey in Save's place (Account Security's card, its from-level variant).
        renderPasskeySetupOffer: renderSetupOffer,
      }}
      operations={charityComplianceOperations}
    >
      <ComplianceSigningLevelCard onDirtyChange={registerDirty} />
    </ComplianceProvider>
  );
}

/**
 * "Sign-in details on signing requests" — everyone the organization's signing requests hold sign-in
 * details for, each opening the removal. Its rows name people as the Team page does, so it reads the
 * roster the AI setting's card reads, and names nobody until it has it.
 */
export function ComplianceSigningPeoplePanel({ orgRef }: SettingsPanelProps) {
  const { pending, errored, standings } = useCharityConsole();
  const moduleStanding = complianceStandingFor({ pending, errored, standings });
  const roster = useOrgMembersExhausted(orgRef, moduleStanding === 'available');
  const members = useMemo(
    () => organizationMembers({ data: roster.data, isError: roster.isError }),
    [roster.data, roster.isError],
  );
  return (
    <ComplianceProvider
      host={{
        orgRef,
        isReady: true,
        isPending: false,
        isError: false,
        error: null,
        moduleStanding,
        ...(members !== undefined ? { organizationMembers: members } : {}),
      }}
      operations={charityComplianceOperations}
    >
      <ComplianceSigningPeopleCard />
    </ComplianceProvider>
  );
}

/** "Givmo’s filing desk" — the organization's agreement for Givmo to file, and turning it off. */
export function ComplianceDeskPanel({ orgRef, registerDirty }: SettingsPanelProps) {
  const { pending, errored, standings } = useCharityConsole();
  const moduleStanding = complianceStandingFor({ pending, errored, standings });
  return (
    <ComplianceProvider
      host={{
        orgRef,
        isReady: true,
        isPending: false,
        isError: false,
        error: null,
        moduleStanding,
      }}
      operations={charityComplianceOperations}
    >
      <DeskStandingCard onDirtyChange={registerDirty} />
    </ComplianceProvider>
  );
}

/** Compliance's declared settings, registered beside its nav in the console's declarations. */
export const COMPLIANCE_SETTINGS: ModuleSettings = {
  slug: COMPLIANCE_SLUG,
  panels: [
    {
      id: 'compliance-ai',
      order: 10,
      requires: COMPLIANCE_OWNER_PERMISSION,
      component: ComplianceAiSettingPanel,
    },
    {
      id: 'compliance-desk',
      order: 15,
      requires: COMPLIANCE_SEND_FOR_SIGNATURE_PERMISSION,
      component: ComplianceDeskPanel,
    },
    {
      id: 'compliance-signing-level',
      order: 20,
      requires: COMPLIANCE_SEND_FOR_SIGNATURE_PERMISSION,
      component: ComplianceSigningLevelPanel,
    },
    {
      id: 'compliance-signing-people',
      order: 25,
      requires: COMPLIANCE_SEND_FOR_SIGNATURE_PERMISSION,
      component: ComplianceSigningPeoplePanel,
    },
  ],
};
